import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { ExternalLink, Image as ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { adminArticlesService } from '@/services/admin-articles.service';
import { ApiError } from '@/services/http';
import { cn } from '@/lib/cn';
import type { ArticleView } from '@/types/api';

/**
 * ArticleThumbnailUploader — admin control for the per-article header /
 * thumbnail image that appears on the Learns landing cards and featured
 * hero. Slotted into the right-hand Sidebar of `AdminEditArticle`.
 *
 * Two affordances, both admin-gated server-side:
 *   - Replace → opens a hidden `<input type="file">` and POSTs the chosen
 *     file via `adminArticlesService.uploadThumbnail` (multipart).
 *   - Remove → DELETEs the stored file and clears `thumbnail_image`.
 *
 * The component is controlled — the parent owns the current article and
 * hands it the refreshed payload after every successful mutation via
 * `onArticleChange`. When `articleId` is null (new article that hasn't
 * been saved yet) we render a hint instead of the upload control, since
 * there's no row to attach the file to.
 */
export interface ArticleThumbnailUploaderProps {
  /** Set to null while the article is unsaved (new editor mode). */
  articleId: string | null;
  thumbnailImage: string | null;
  articleTitle: string;
  onArticleChange: (article: ArticleView) => void;
  className?: string;
}

const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];
const MAX_SIZE_BYTES = 5 * 1024 * 1024; // 5 MB — mirrors the cover/vocab uploaders

export function ArticleThumbnailUploader({
  articleId,
  thumbnailImage,
  articleTitle,
  onArticleChange,
  className,
}: ArticleThumbnailUploaderProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);

  const busy = uploading || removing;

  const handlePick = () => {
    if (busy || !articleId) return;
    inputRef.current?.click();
  };

  const handleFile = async (file: File) => {
    if (!articleId) return;
    if (!ACCEPTED_TYPES.includes(file.type)) {
      toast.error('Thumbnail must be a PNG, JPEG, WEBP, or GIF image.');
      return;
    }
    if (file.size > MAX_SIZE_BYTES) {
      toast.error('Thumbnail files must be 5MB or smaller.');
      return;
    }
    setUploading(true);
    try {
      const updated = await adminArticlesService.uploadThumbnail(articleId, file);
      onArticleChange(updated);
      toast.success('Thumbnail updated.');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not upload the thumbnail.');
    } finally {
      setUploading(false);
      // Reset the input so picking the same file again still fires `onChange`.
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleRemove = async () => {
    if (busy || !articleId || !thumbnailImage) return;
    setRemoving(true);
    try {
      const updated = await adminArticlesService.deleteThumbnail(articleId);
      onArticleChange(updated);
      toast.success('Thumbnail removed.');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not remove the thumbnail.');
    } finally {
      setRemoving(false);
    }
  };

  // Unsaved article: prompt to save first instead of accepting an upload
  // that has nowhere to land. Same gating pattern as QuizSidebarLink.
  if (!articleId) {
    return (
      <div className={cn('flex flex-col gap-2', className)}>
        <div className="or-kicker text-or-slate-500">Thumbnail</div>
        <div className="flex items-center gap-2 rounded-lg border border-dashed border-or-slate-300 bg-or-cream-50 px-3 py-3 text-[12px] text-or-slate-600">
          <ImageIcon className="h-4 w-4 text-or-slate-400" aria-hidden />
          Save the article first to upload a thumbnail.
        </div>
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <div className="or-kicker text-or-slate-500">Thumbnail</div>
      {thumbnailImage ? (
        <div className="overflow-hidden rounded-xl border border-or-slate-200 bg-white">
          <div className="aspect-[16/10] w-full bg-or-slate-50">
            <img
              src={thumbnailImage}
              alt={`${articleTitle || 'Article'} thumbnail`}
              className="h-full w-full object-cover"
            />
          </div>
        </div>
      ) : (
        <div
          className="flex aspect-[16/10] w-full flex-col items-center justify-center rounded-xl border border-dashed border-or-slate-300 bg-or-cream-50 px-3 text-center text-[11px] text-or-slate-500"
          aria-hidden
        >
          <ImageIcon className="mb-1 h-4 w-4 text-or-slate-400" aria-hidden />
          No thumbnail uploaded yet
        </div>
      )}
      <p className="text-[11px] leading-relaxed text-or-slate-500">
        {thumbnailImage
          ? 'Replace the current thumbnail or remove it to fall back to the tone-stripe placeholder.'
          : 'Upload a PNG, JPEG, WEBP, or GIF. Max 5MB. Shown on the Learns grid and featured hero.'}
      </p>
      <div className="flex flex-wrap gap-2">
        <Button variant="primary" size="sm" onClick={handlePick} disabled={busy}>
          {uploading ? 'Uploading…' : thumbnailImage ? 'Replace' : 'Upload thumbnail'}
        </Button>
        {thumbnailImage ? (
          <>
            <a
              href={thumbnailImage}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 rounded-full border border-or-slate-200 bg-white px-3 py-1 text-[12px] font-semibold text-or-slate-700 hover:bg-or-slate-50"
              title="Open the thumbnail file in a new tab"
            >
              <ExternalLink className="h-3 w-3" aria-hidden />
              Open
            </a>
            <Button
              variant="danger"
              size="sm"
              onClick={handleRemove}
              disabled={busy}
              title="Clear the uploaded thumbnail"
            >
              {removing ? 'Removing…' : 'Remove'}
            </Button>
          </>
        ) : null}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_TYPES.join(',')}
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) void handleFile(file);
        }}
      />
    </div>
  );
}
